import { useMemo } from 'react';

// ─── Live preview mock ────────────────────────────────────────────────────────
function buildMockPreview(settings, listingMode) {
  const { tone, listingStyle, spacing, condition } = settings;

  const playful = listingStyle !== 'minimalist';
  const sep = spacing === 'spaced' ? '\n\n' : '\n';
  const e = (emoji) => (playful ? emoji + ' ' : '');

  const item =
    listingMode === 2 ? 'Coffee Table + Side Table Bundle' :
    listingMode === 3 ? 'Item per photo' :
    'Coffee Table';

  const titleMap = {
    short_sharp: `${e('📦')}${item} – ${condition || 'Good'} Cond.`,
    regular:     `${e('✨')}${item} – ${condition || 'Good'} Condition`,
    laugh:       `${e('😂')}This Coffee Table Walked So Your Living Room Could Run`,
  };
  const title = titleMap[tone] || titleMap.regular;

  const features = ['Solid timber frame', 'Minimal wear', 'Easy to transport'];
  let featBlock;
  if (playful) {
    const icons = ['🪵', '✨', '🚗'];
    featBlock = features.map((f, i) => `${icons[i]} ${f}`).join('\n');
  } else {
    featBlock = features.map((f) => `• ${f}`).join('\n');
  }

  const bodyMap = {
    short_sharp: `${featBlock}${sep}${e('✨')}Condition: ${condition || 'Good'}\nNo holds. First in, first served.`,
    regular:     `${e('🛋️')}Great ${condition?.toLowerCase() || 'good'} condition coffee table, perfect for any home.${sep}${featBlock}${sep}Great value — don't miss out!`,
    laugh:       `${e('☕')}Tired of putting your coffee on the floor like some kind of animal? This table has you covered.${sep}${featBlock}${sep}${e('🤣')}Warning: may cause excessive furniture rearranging.`,
  };
  const desc = bodyMap[tone] || bodyMap.regular;

  if (listingMode === 3) {
    return {
      title: 'Bulk Listing Preview',
      description: `Photo 1:\nTITLE: ${title}\nDESCRIPTION: ${desc}\n\nPhoto 2:\nTITLE: (Next item from your photos)\nDESCRIPTION: (Generated from photo 2)`,
    };
  }

  return { title, description: desc };
}

// ─── SettingsView ─────────────────────────────────────────────────────────────
export default function SettingsView({ settings, onSettingsChange, listingMode }) {
  function update(key, value) {
    onSettingsChange({ ...settings, [key]: value });
  }

  const preview = useMemo(
    () => buildMockPreview(settings, listingMode),
    [settings, listingMode]
  );

  return (
    <div className="sv">

      {/* ── Tone ── */}
      <section className="sv-section">
        <h2 className="sv-section-title">Tone</h2>
        <div className="sv-tone-group">
          {[
            { value: 'short_sharp', label: 'Short & Sharp',  icon: '⚡', desc: 'Bullet points, no filler' },
            { value: 'regular',     label: 'Regular',         icon: '🙂', desc: 'Friendly & professional' },
            { value: 'laugh',       label: 'Bit of a Laugh',  icon: '😂', desc: 'Witty & attention-grabbing' },
          ].map(({ value, label, icon, desc }) => (
            <button
              key={value}
              className={`sv-tone-card ${settings.tone === value ? 'sv-tone-card--active' : ''}`}
              onClick={() => update('tone', value)}
            >
              <span className="sv-tone-icon">{icon}</span>
              <span className="sv-tone-label">{label}</span>
              <span className="sv-tone-desc">{desc}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Listing Style ── */}
      <section className="sv-section">
        <h2 className="sv-section-title">Listing Style</h2>
        <p className="sv-section-hint">Controls how the listing text is formatted.</p>
        <div className="sv-style-group">
          {[
            { value: 'playful',     label: 'Playful',     icon: '✨', desc: 'Emoji headings & bullets' },
            { value: 'minimalist',  label: 'Minimalist',  icon: '▪️', desc: 'Clean, no emojis · • bullets' },
          ].map(({ value, label, icon, desc }) => (
            <button
              key={value}
              className={`sv-tone-card ${settings.listingStyle === value ? 'sv-tone-card--active' : ''}`}
              onClick={() => update('listingStyle', value)}
            >
              <span className="sv-tone-icon">{icon}</span>
              <span className="sv-tone-label">{label}</span>
              <span className="sv-tone-desc">{desc}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Paragraph Spacing ── */}
      <section className="sv-section">
        <h2 className="sv-section-title">Paragraph Spacing</h2>
        <div className="sv-spacing-group">
          {[
            { value: 'spaced',  label: 'Spaced',  icon: '↕️', desc: 'Double line breaks between sections' },
            { value: 'compact', label: 'Compact', icon: '▤',  desc: 'Tight single returns, minimal whitespace' },
          ].map(({ value, label, icon, desc }) => (
            <button
              key={value}
              className={`sv-tone-card ${settings.spacing === value ? 'sv-tone-card--active' : ''}`}
              onClick={() => update('spacing', value)}
            >
              <span className="sv-tone-icon">{icon}</span>
              <span className="sv-tone-label">{label}</span>
              <span className="sv-tone-desc">{desc}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Condition ── */}
      <section className="sv-section">
        <h2 className="sv-section-title">Item Condition</h2>
        <div className="sv-condition-group">
          {['Brand New', 'Like New', 'Good', 'Fair'].map((c) => (
            <button
              key={c}
              className={`sv-chip ${settings.condition === c ? 'sv-chip--active' : ''}`}
              onClick={() => update('condition', c)}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* ── Live Preview ── */}
      <section className="sv-section sv-preview-section">
        <div className="sv-preview-header">
          <h2 className="sv-section-title" style={{ margin: 0 }}>Live Preview</h2>
          <span className="sv-preview-badge">Coffee Table</span>
        </div>
        <p className="sv-section-hint">Updates instantly as you change settings above. Price, dimensions &amp; location are added per-listing under “Quick Add Details”.</p>

        <div className="sv-preview-card">
          <div className="sv-preview-block">
            <span className="sv-preview-label">TITLE</span>
            <p className="sv-preview-title">{preview.title}</p>
          </div>
          <div className="sv-preview-divider" />
          <div className="sv-preview-block">
            <span className="sv-preview-label">DESCRIPTION</span>
            <p className="sv-preview-desc">{preview.description}</p>
          </div>
        </div>
      </section>

      <style>{`
        .sv { display: flex; flex-direction: column; gap: 0; padding-bottom: 24px; }

        .sv-section { padding: 20px 0; border-bottom: 0.5px solid var(--color-border); }
        .sv-section:last-child { border-bottom: none; }

        .sv-section-title {
          font-size: 11px; font-weight: 500; letter-spacing: 0.07em;
          text-transform: uppercase; color: var(--color-text-hint); margin-bottom: 12px;
        }

        .sv-optional { font-weight: 400; text-transform: none; letter-spacing: 0; font-size: 12px; color: var(--color-text-hint); }

        .sv-section-hint { font-size: 12px; color: var(--color-text-hint); margin-top: -8px; margin-bottom: 12px; line-height: 1.6; }

        /* ── Tone cards */
        .sv-tone-group { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }

        /* ── Listing style cards */
        .sv-style-group { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }

        .sv-tone-card {
          display: flex; flex-direction: column; align-items: center; gap: 4px;
          padding: 12px 8px; background: var(--color-bg-card);
          border: 0.5px solid var(--color-border); border-radius: var(--radius-md);
          cursor: pointer; transition: border-color 0.15s, background 0.15s;
          -webkit-tap-highlight-color: transparent; font-family: inherit; text-align: center;
        }
        .sv-tone-card--active { border-color: var(--color-primary); background: var(--color-primary-light); }

        .sv-tone-icon  { font-size: 22px; }
        .sv-tone-label { font-size: 12px; font-weight: 500; color: var(--color-text-heading); }
        .sv-tone-desc  { font-size: 10px; color: var(--color-text-hint); line-height: 1.3; }

        /* ── Spacing selector */
        .sv-spacing-group { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }

        /* ── Condition chips */
        .sv-condition-group { display: flex; gap: 8px; flex-wrap: wrap; }

        .sv-chip {
          padding: 7px 16px; background: var(--color-bg-page);
          border: 0.5px solid var(--color-border); border-radius: 9999px;
          color: var(--color-text-secondary); font-family: inherit; font-size: 13px;
          font-weight: 400; cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .sv-chip--active { border-color: var(--color-primary); background: var(--color-primary-light); color: var(--color-primary); font-weight: 500; }

        /* ── Live Preview */
        .sv-preview-section { border-bottom: none; }
        .sv-preview-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .sv-preview-badge {
          font-size: 10px; font-weight: 500; letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--color-text-hint); background: var(--color-bg-page);
          border: 0.5px solid var(--color-border); padding: 3px 8px; border-radius: 9999px;
        }
        .sv-preview-card { background: var(--color-bg-card); border: 0.5px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; }
        .sv-preview-block { padding: 14px 16px; }
        .sv-preview-divider { height: 0.5px; background: var(--color-border); }
        .sv-preview-label { display: block; font-size: 9px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-hint); margin-bottom: 6px; }
        .sv-preview-title { font-size: 14px; font-weight: 500; color: var(--color-text-heading); line-height: 1.35; }
        .sv-preview-desc  { font-size: 13px; color: var(--color-text-secondary); line-height: 1.6; white-space: pre-wrap; }
      `}</style>
    </div>
  );
}
